import { memo, useCallback, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { playButtonClickSound } from '@wg/web2clientapi/sound';
import { ContextMenu, TableBodyCell, TableRow } from '@wg/wows-react-uikit';

import { clanIsFull } from '~/helpers/clan';
import { getPositionY } from '~/helpers/contextMenu';
import { floats, percent, thousands } from '~/helpers/formatting';
import { t } from '~/helpers/localization';

import { Clan, StatisticsValue } from '~/UIKit';

import type {
  IItem as ContextMenuItem,
  IProps as ContextMenuProps,
} from '@wg/wows-react-uikit/components/ContextMenu/ContextMenu';
import type { ICurrentAccountState } from '~/store/slices/currentAccountSlice';
import type { ClanInSearch, ClanSearchResultItem } from '~/types/declaration';

type ContextMenuContext = ContextMenuProps['context'];

type ContextMenuPosition = { x: number; y: number };

interface SearchResultTableItemProps {
  clan: ClanSearchResultItem;
  term: string;
  currentAccount: ICurrentAccountState;
  onClick: (clanId: number) => void;
  joinClan: (clan: ClanInSearch) => void;
}

const SearchResultTableItem = ({ clan, term, currentAccount, onClick, joinClan }: SearchResultTableItemProps) => {
  const [isContextMenuActive, setIsContextMenuActive] = useState(false);
  const [contextMenuContext, setContextMenuContext] = useState<ContextMenuContext>([]);
  const [contextMenuPosition, setContextMenuPosition] = useState<ContextMenuPosition>({ x: 0, y: 0 });

  const tableFields = clan?.table_fields ?? {};

  const isOwn = currentAccount?.clanId === clan?.id;

  const isHiddenStatistics = useMemo(() => {
    let hidden = Boolean(tableFields?.is_hidden_statistics);

    if (!hidden && clan?.members_count === 1) {
      hidden = Boolean(clan?.is_hidden_by_account);
    }

    return hidden;
  }, [clan?.is_hidden_by_account, clan?.members_count, tableFields?.is_hidden_statistics]);

  const getContextMenuContext = useCallback((): ContextMenuContext => {
    const items: ContextMenuItem[] = [
      {
        name: t('Профиль клана'),
        value: 'CLAN_PROFILE',
        disabled: false,
      },
    ];

    if (!currentAccount?.clanId) {
      items.push({
        name: t('Вступить в клан'),
        value: 'JOIN_CLAN',
        disabled: Boolean(currentAccount?.inClanCooldownTill) || clanIsFull(clan),
      });
    }

    return [
      {
        isNeedDivider: false,
        items: items,
      },
    ];
  }, [clan, currentAccount?.clanId, currentAccount?.inClanCooldownTill]);

  const onRowClick = useCallback(() => {
    void playButtonClickSound();
    onClick(clan.id);
  }, [clan.id, onClick]);

  const onContextMenu = useCallback(
    (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
      const context = getContextMenuContext();
      const clientY = getPositionY(event, context);

      setContextMenuContext(context);
      setContextMenuPosition({
        x: event.clientX,
        y: clientY,
      });
    },
    [getContextMenuContext],
  );

  const onContextMenuStateChange = useCallback((active: boolean) => {
    setIsContextMenuActive(active);
  }, []);

  const onContextMenuItemClick = useCallback(
    (action: string | object) => {
      if (action === 'CLAN_PROFILE') {
        onClick(clan.id);
        return;
      }

      if (action === 'JOIN_CLAN') {
        joinClan({
          id: clan?.id,
          name: clan?.name,
          tag: clan?.tag,
          color: clan?.hex_color,
          leveling: clan?.leveling,
        });
      }
    },
    [clan, joinClan, onClick],
  );

  return (
    <TableRow
      isActive={isContextMenuActive}
      isHovering
      isOwn={isOwn}
      onContextMenu={onContextMenu}
      onClick={onRowClick}
    >
      {createPortal(
        <ContextMenu
          context={contextMenuContext}
          position={contextMenuPosition}
          onItemClick={onContextMenuItemClick}
          onContextMenuStateChange={onContextMenuStateChange}
          isDemo={false}
        />,
        document.querySelector('body')!,
      )}

      <TableBodyCell modify="basis">
        <Clan
          clanTag={clan?.tag}
          clanName={clan?.name}
          clanColor={clan?.hex_color}
          highlight={term}
          isOwn={isOwn}
          disableTooltip={isContextMenuActive}
        />
      </TableBodyCell>

      <TableBodyCell modify="small">{clan?.members_count}</TableBodyCell>

      <TableBodyCell modify="right">
        <StatisticsValue
          isClan
          value={thousands(tableFields?.average_battles)}
          isHidden={isHiddenStatistics}
          disableTooltip={isContextMenuActive}
        />
      </TableBodyCell>

      <TableBodyCell modify="right">
        <StatisticsValue
          isClan
          value={percent(tableFields?.average_wins_percent)}
          isHidden={isHiddenStatistics}
          disableTooltip={isContextMenuActive}
        />
      </TableBodyCell>

      <TableBodyCell modify="right">
        <StatisticsValue
          isClan
          value={thousands(tableFields?.average_xp)}
          isHidden={isHiddenStatistics}
          disableTooltip={isContextMenuActive}
        />
      </TableBodyCell>

      <TableBodyCell modify="right">
        <StatisticsValue
          isClan
          value={thousands(tableFields?.average_damage)}
          isHidden={isHiddenStatistics}
          disableTooltip={isContextMenuActive}
        />
      </TableBodyCell>

      <TableBodyCell modify="right">
        <StatisticsValue
          isClan
          value={floats(tableFields?.average_frags, 2)}
          isHidden={isHiddenStatistics}
          disableTooltip={isContextMenuActive}
        />
      </TableBodyCell>
    </TableRow>
  );
};

export default memo(SearchResultTableItem);
